/**
 * A pilot's blockers (PRD §5.4).
 *
 * Unlike the KPI table and the milestones, blockers are **appended and resolved
 * individually** rather than saved as a set. That is not an inconsistency: a
 * blocker's raised-at is a fact about the moment one person raised it, and a
 * whole-set replace would rewrite everyone else's timestamps.
 *
 * Reopening is offered because "resolved" is a judgement that turns out wrong
 * often enough; without it the only recovery is a second blocker with the same
 * text, which corrupts the open count the list page shows.
 *
 * Open blockers come first — an open one is a task and a resolved one is
 * history, and a list ordered purely by date buries what still needs doing.
 * The server already sorts them that way.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.2, PRD §5.4
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PilotBlocker } from '@nexus/contracts';

import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card, CardBody, CardHead } from '../../../components/ui/Card';
import { Empty } from '../../../components/ui/Empty';
import { Input } from '../../../components/ui/Input';
import { Kicker } from '../../../components/ui/Kicker';
import { formatDate } from '../../../lib/format';

export interface BlockerListProps {
  blockers: readonly PilotBlocker[];
  canEdit: boolean;
  /** Appends one blocker. */
  onAdd: (description: string) => void;
  /** Resolves or reopens one. */
  onSetResolved: (blockerId: string, resolved: boolean) => void;
  isSaving: boolean;
}

export function BlockerList({
  blockers,
  canEdit,
  onAdd,
  onSetResolved,
  isSaving,
}: BlockerListProps): React.JSX.Element {
  const { t } = useTranslation();
  const [description, setDescription] = useState('');

  const openCount = blockers.filter((blocker) => blocker.resolvedAt === null).length;
  const trimmed = description.trim();

  const submit = (event: React.SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();

    /* Guarded as well as disabling the button. A disabled submit button does
       not reliably stop Enter from submitting the form across browsers, and
       adding a blank blocker would put an unreadable row in front of everyone
       reviewing the pilot.

       Unreachable through the UI in jsdom, which is why it is excluded rather
       than tested: `userEvent` respects the disabled button, so the only way
       to exercise it would be to dispatch a synthetic submit — a test of the
       test, not of the guard. */
    /* v8 ignore next */
    if (trimmed === '') return;

    onAdd(trimmed);
    setDescription('');
  };

  return (
    <Card>
      <CardHead>
        <Kicker as="h2">
          {/* The OPEN count, not the total: a pilot with nine resolved
              blockers and none open is not blocked, and a bare total would
              say it was. */}
          {openCount === 0
            ? t('pilot.blockers.title')
            : t('pilot.blockers.titleWithCount', { count: openCount })}
        </Kicker>
      </CardHead>

      <CardBody flush>
        {blockers.length === 0 ? (
          <Empty title={t('pilot.blockers.emptyTitle')}>
            {t('pilot.blockers.emptyBody')}
          </Empty>
        ) : (
          <ul>
            {blockers.map((blocker) => {
              const resolved = blocker.resolvedAt !== null;

              return (
                <li
                  key={blocker.id}
                  className="border-gray-100 gap-s3 px-s3 flex items-start border-b py-2.5 last:border-b-0"
                >
                  <span className="min-w-0 flex-1">
                    <span
                      className={
                        /* Struck through as well as labelled: the state is in
                           the badge, and the line-through is a second,
                           redundant signal rather than the only one. */
                        resolved
                          ? 'text-gray-400 block text-[13px] line-through'
                          : 'text-body block text-[13px]'
                      }
                    >
                      {blocker.description}
                    </span>
                    <span className="text-gray-400 mt-0.5 block text-xs font-light tabular-nums">
                      {resolved
                        ? t('pilot.blockers.resolvedOn', {
                            when: formatDate(blocker.resolvedAt),
                          })
                        : t('pilot.blockers.raisedOn', {
                            when: formatDate(blocker.raisedAt),
                          })}
                    </span>
                  </span>

                  <Badge tone={resolved ? 'mute' : 'warn'}>
                    {resolved ? t('pilot.blockers.resolved') : t('pilot.blockers.open')}
                  </Badge>

                  {canEdit ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={isSaving}
                      onClick={() => {
                        onSetResolved(blocker.id, !resolved);
                      }}
                    >
                      {resolved
                        ? t('pilot.blockers.reopen')
                        : t('pilot.blockers.markResolved')}
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}

        {canEdit ? (
          /* A form, so Enter submits — raising a blocker is a one-field action
             and reaching for the mouse for it is friction nobody accepts when
             they are already annoyed enough to record a blocker. */
          <form onSubmit={submit} className="border-gray-100 gap-s2 flex border-t p-s3">
            <label className="flex-1">
              <span className="sr-only">{t('pilot.blockers.newLabel')}</span>
              <Input
                value={description}
                onChange={(event) => {
                  setDescription(event.target.value);
                }}
                placeholder={t('pilot.blockers.newPlaceholder')}
              />
            </label>
            <Button type="submit" size="sm" disabled={isSaving || trimmed === ''}>
              {t('pilot.blockers.add')}
            </Button>
          </form>
        ) : null}
      </CardBody>
    </Card>
  );
}
